import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';

// Images
import ok_n from '../../Common/Images/ok_n.gif';
import ok_o from '../../Common/Images/ok_o.gif';
import ok_d from '../../Common/Images/ok_d.gif';
import cancel_n from '../../Common/Images/cancel_n.gif';
import cancel_o from '../../Common/Images/cancel_o.gif';
import cancel_d from '../../Common/Images/cancel_d.gif';
import bgImage from '../../Common/Images/MasterBKG.png';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface CountryOption {
    major: string;
    minor: string;
}

interface ParentDealerOption {
    SchoolCode: string;
    Name: string;
}

const EL_AddNewSchoolCode: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determine return path
    const queryParams = new URLSearchParams(location.search);
    const calledFrom = location.state?.CalledFrom || queryParams.get('CalledFrom') || '';
    console.log('EL_AddNewSchoolCode called from:', calledFrom);

    // Form State
    const [category, setCategory] = useState<string>('DLR');
    const [code, setCode] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [country, setCountry] = useState<string>('');
    const [adminLogin, setAdminLogin] = useState<string>('');
    const [adminPwd, setAdminPwd] = useState<string>('');
    const [parentDealer, setParentDealer] = useState<string>('');

    // Data Source State
    const [countries, setCountries] = useState<string[]>([]);
    const [parentDealers, setParentDealers] = useState<ParentDealerOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [message, setMessage] = useState<{ text: string, type: 'error' | 'success' | 'info' } | null>(null);

    // Button Hover States
    const [btnOkSrc, setBtnOkSrc] = useState(ok_n);
    const [btnCancelSrc, setBtnCancelSrc] = useState(cancel_n);

    // --- Helper: Format Date as dd/mm/yyyy (Strictly for Backend) ---
    const formatDateLegacy = (date: Date): string => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}api/get_countries/`);
                if (!response.ok) throw new Error('Failed to load countries');

                const data: { countries: string[] } = await response.json();

                const sortedCountries = [...data.countries].sort((a, b) =>
                    a.localeCompare(b)
                );

                setCountries(sortedCountries);

                if (sortedCountries.length > 0) {
                    setCountry(prev => prev || sortedCountries[0]);
                }

            } catch (error) {
                console.error(error);
                setMessage({ text: 'Error loading countries.', type: 'error' });
            }
        };

        fetchCountries();
    }, []);

    // --- 2. Load Parent Dealers ---
    useEffect(() => {
        if (category === 'DSR' && country) {
            const fetchDealers = async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}api/manage-schools/parent_dealers/?country=${encodeURIComponent(country)}`);
                    if (response.ok) {
                        const data = await response.json();
                        setParentDealers(data);
                    }
                } catch (error) {
                    console.error("Error loading parent dealers", error);
                }
            };
            fetchDealers();
        } else {
            setParentDealers([]);
        }
    }, [country, category]);

    // --- 3. Validation ---
    const validateForm = (): boolean => {
        setMessage(null);

        const trimmedCode = code.trim();
        const trimmedLogin = adminLogin.trim();
        const trimmedPwd = adminPwd.trim();
        const trimmedName = name.trim();

        if (trimmedCode === '') {
            setMessage({ text: "Please enter Code", type: 'error' });
            return false;
        }
        if (trimmedCode.length < 3) {
            setMessage({ text: "Code should contain minimum 3 characters", type: 'error' });
            return false;
        }
        const validCodeRegex = /^[A-Z\-_]+$/i;
        if (!validCodeRegex.test(trimmedCode)) {
            setMessage({ text: "Code should contain only alphabets (A-Z), '-' or '_'", type: 'error' });
            return false;
        }
        if (trimmedCode.toUpperCase().startsWith('MD_')) {
            setMessage({ text: "School code starting with MD_ is reserved for MathsDoctor - elearnWeb Link.", type: 'error' });
            return false;
        }
        if (trimmedName === '') {
            setMessage({ text: "Please enter Name", type: 'error' });
            return false;
        }
        if (!country) {
            setMessage({ text: "Please select a Country", type: 'error' });
            return false;
        }
        if (trimmedLogin === '') {
            setMessage({ text: "Please enter Admin Login ID", type: 'error' });
            return false;
        }
        if (trimmedPwd === '') {
            setMessage({ text: "Please enter Admin password", type: 'error' });
            return false;
        }
        if (trimmedCode.includes("'") || trimmedLogin.includes("'") || trimmedPwd.includes("'")) {
            setMessage({ text: "Input fields should not contain Apostrophe.", type: 'error' });
            return false;
        }
        if (category === 'DSR' && parentDealer === '') {
            setMessage({ text: "Please select Parent Dealer", type: 'error' });
            return false;
        }

        return true;
    };

    // --- 4. Save Handler ---
    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);

        const today = new Date();
        const nextYear = new Date(new Date().setFullYear(today.getFullYear() + 1));

        const payload = {
            schoolCode: code.trim().toUpperCase(),
            schoolName: name.trim(),
            country: country,
            category: (category === 'DSR') ? 'DLR' : category,
            parentDealer: (category === 'DSR') ? parentDealer : '',
            adminLogin: adminLogin.trim(),
            adminPass: adminPwd.trim(),

            // FIXED: Using custom helper to ensure dd/mm/yyyy format
            regDate: formatDateLegacy(today),
            accessStart: formatDateLegacy(today),
            accessEnd: formatDateLegacy(nextYear),

            maxUsers: 0,
            maxSeats: 0,
            startTime: '00:00',
            endTime: '23:59',
            allowHomeUse: false
        };

        try {
            const response = await fetch(`${API_BASE_URL}api/manage-schools/create/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (response.ok && result.status === 'success') {
                navigate(
                    `/WebAdmin/SCHOOLS/EL_SchoolDetails?SchoolCode=${code.trim().toUpperCase()}&CalledFrom=${calledFrom}`
                );
            } else {
                setMessage({
                    text: result.error || "An error occurred while saving.",
                    type: 'error'
                });
            }
        } catch (error) {
            setMessage({ text: "Network error occurred.", type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // --- 5. Navigation ---
    const handleCancel = () => {
        // if (calledFrom === 'EL_ListSchools') {
        //     navigate('/WebAdmin/SCHOOLS/EL_ListSchools');
        // } else {
        //     navigate('/SysAdmin');
        // }
        window.history.back();
    };

    const handleRadioChange = (val: string) => {
        setCategory(val);
        if (val !== 'DSR') setParentDealer('');
    };

    return (
        <div style={{
            backgroundColor: '#214461',
            minHeight: '100vh',
            margin: 0,
            padding: 0,
            display: 'flex',
            justifyContent: 'center',
            fontFamily: 'Arial, sans-serif'
        }}>
            <div style={{
                backgroundImage: `url(${bgImage})`,
                width: '800px',
                height: '600px',
                // marginTop: '50px',
                position: 'relative',
                textAlign: 'center'
            }}>
                <div style={{ height: '200px' }}></div>

                {/* Header Table */}
                <table style={{ width: '80%', margin: '0 auto', borderSpacing: '1px' }} cellPadding="5">
                    <tbody>
                        <tr>
                            <td style={{
                                color: 'white',
                                fontWeight: 'bold',
                                backgroundColor: 'blue',
                                textAlign: 'left'
                            }}>
                                Adding new School / Library / ISP / eDealer / Distributor
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Error/Message Display */}
                {message && (
                    <CustomAlert
                        message={message.text}
                        onClose={() => setMessage(null)}
                    />
                )}

                {/* Main Form Table */}
                <table style={{
                    width: '80%',
                    margin: '0 auto',
                    border: 'solid 1px black',
                    backgroundColor: 'AliceBlue',
                    borderSpacing: 0
                }} cellPadding="5">
                    <tbody>
                        <tr>
                            <td style={{ width: '20%', verticalAlign: 'top', textAlign: 'left', fontSize: '11pt' }}>
                                <div style={{ marginBottom: '10px' }}>
                                    <label>
                                        <input
                                            type="radio"
                                            name="catg"
                                            checked={category === 'SCH'}
                                            onChange={() => handleRadioChange('SCH')}
                                        /> School
                                    </label>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                    <label>
                                        <input
                                            type="radio"
                                            name="catg"
                                            checked={category === 'LIB'}
                                            onChange={() => handleRadioChange('LIB')}
                                        /> Library
                                    </label>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                    <label>
                                        <input
                                            type="radio"
                                            name="catg"
                                            checked={category === 'ISP'}
                                            onChange={() => handleRadioChange('ISP')}
                                        /> ISP
                                    </label>
                                </div>
                                <div style={{ marginBottom: '10px' }}>
                                    <label>
                                        <input
                                            type="radio"
                                            name="catg"
                                            checked={category === 'DLR'}
                                            onChange={() => handleRadioChange('DLR')}
                                        /> eDealer
                                    </label>
                                </div>
                                <div>
                                    <label>
                                        <input
                                            type="radio"
                                            name="catg"
                                            checked={category === 'DSR'}
                                            onChange={() => handleRadioChange('DSR')}
                                        /> Distributor
                                    </label>
                                </div>
                            </td>

                            <td style={{
                                width: '80%',
                                verticalAlign: 'top',
                                borderLeft: 'solid 1px black',
                                fontSize: '11pt'
                            }}>
                                <table style={{ width: '100%', borderSpacing: 0 }} cellPadding="2">
                                    <tbody>
                                        <tr>
                                            <td style={{ width: '35%', textAlign: 'right' }}>Code</td>
                                            <td style={{ width: '65%', textAlign: 'center' }}>
                                                <input
                                                    type="text"
                                                    maxLength={10}
                                                    style={{ width: '90%', fontSize: '12pt', border: '1px solid #c4c4c4' }}
                                                    value={code}
                                                    onChange={(e) => setCode(e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: 'right' }}>Name</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="text"
                                                    maxLength={50}
                                                    style={{ width: '90%', fontSize: '12pt', border: '1px solid #c4c4c4' }}
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: 'right' }}>Country</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <select
                                                    style={{ width: '90%', fontSize: '12pt', border: '1px solid #c4c4c4' }}
                                                    value={country}
                                                    onChange={(e) => setCountry(e.target.value)}
                                                >
                                                    {/* <option value="">--Select--</option> */}
                                                    {countries.map((c, idx) => (
                                                        <option key={idx} value={c}>{c}</option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: 'right' }}>Admin Login</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="text"
                                                    maxLength={50}
                                                    style={{ width: '90%', fontSize: '12pt', border: '1px solid #c4c4c4' }}
                                                    value={adminLogin}
                                                    onChange={(e) => setAdminLogin(e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                        <tr>
                                            <td style={{ textAlign: 'right' }}>Admin Password</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="text"
                                                    maxLength={50}
                                                    style={{ width: '90%', fontSize: '12pt', border: '1px solid #c4c4c4' }}
                                                    value={adminPwd}
                                                    onChange={(e) => setAdminPwd(e.target.value)}
                                                />
                                            </td>
                                        </tr>

                                        <tr style={{ display: category === 'DSR' ? 'table-row' : 'none' }}>
                                            <td style={{ textAlign: 'right' }}>Parent Dealer</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <select
                                                    style={{ width: '90%', fontSize: '12pt', border: '1px solid #c4c4c4' }}
                                                    value={parentDealer}
                                                    onChange={(e) => setParentDealer(e.target.value)}
                                                >
                                                    <option value="">--Select--</option>
                                                    {parentDealers.map((pd, idx) => (
                                                        <option key={idx} value={pd.SchoolCode}>
                                                            {pd.Name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>

                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* Buttons */}
                <table style={{ width: '80%', margin: '0 auto' }} cellSpacing="0" cellPadding="5">
                    <tbody>
                        <tr><td colSpan={2} style={{ height: '20px' }}></td></tr>
                        <tr>
                            <td colSpan={2} style={{ textAlign: 'center' }}>
                                <img
                                    src={btnOkSrc}
                                    alt="OK"
                                    onMouseOver={() => setBtnOkSrc(ok_o)}
                                    onMouseOut={() => setBtnOkSrc(ok_n)}
                                    onMouseDown={() => setBtnOkSrc(ok_d)}
                                    onMouseUp={() => setBtnOkSrc(ok_n)}
                                    onClick={handleSave}
                                    style={{ cursor: 'pointer', marginRight: '20px' }}
                                />
                                <img
                                    src={btnCancelSrc}
                                    alt="Cancel"
                                    onMouseOver={() => setBtnCancelSrc(cancel_o)}
                                    onMouseOut={() => setBtnCancelSrc(cancel_n)}
                                    onMouseDown={() => setBtnCancelSrc(cancel_d)}
                                    onMouseUp={() => setBtnCancelSrc(cancel_n)}
                                    onClick={handleCancel}
                                    style={{ cursor: 'pointer' }}
                                />
                            </td>
                        </tr>
                        <tr><td colSpan={2} style={{ height: '10px' }}></td></tr>
                    </tbody>
                </table>

                {loading && <Loading />}

            </div>
        </div>
    );
}

export default EL_AddNewSchoolCode;